/**
 * @typedef {object} WholesaleVariant
 * @property {string} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {string} priceFormatted
 * @property {string | null} compareAtPriceFormatted
 * @property {string} url
 * @property {{ min?: number, max?: number | null, increment?: number }} quantityRule
 */

/**
 * @typedef {Element & { updateConstraints?: (min: string, max: string | null, step: string) => void }} QuantitySelector
 */

class WholesaleProductCard extends HTMLElement {
  /** @type {WholesaleVariant[]} */
  variants = [];

  /** @type {HTMLInputElement | null} */
  variantIdInput = null;

  /** @type {Element | null} */
  priceElement = null;

  /** @type {Element | null} */
  comparePriceElement = null;

  /** @type {HTMLAnchorElement[]} */
  productLinks = [];

  /** @type {QuantitySelector | null} */
  quantitySelector = null;

  /** @type {HTMLInputElement | null} */
  quantityInput = null;

  /** @type {HTMLButtonElement | null} */
  addToCartButton = null;

  connectedCallback() {
    this.variants = this.#readVariants();
    this.variantIdInput = this.querySelector('[data-wholesale-variant-id]');
    this.priceElement = this.querySelector('[data-wholesale-price]');
    this.comparePriceElement = this.querySelector('[data-wholesale-compare-price]');
    this.productLinks = Array.from(this.querySelectorAll('[data-wholesale-product-link]')).filter(
      (link) => link instanceof HTMLAnchorElement
    );
    this.quantitySelector = /** @type {QuantitySelector | null} */ (this.querySelector('quantity-selector-component'));
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.addToCartButton = this.querySelector('[ref="addToCartButton"]');

    this.addEventListener('change', this.#handleOptionChange);
    this.#syncToSelectedOptions();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleOptionChange);
  }

  /**
   * @returns {WholesaleVariant[]}
   */
  #readVariants() {
    const script = this.querySelector('[data-wholesale-variants-json]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse wholesale product variants', error);
      return [];
    }
  }

  #handleOptionChange = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-wholesale-option]')) return;

    this.#syncToSelectedOptions();
  };

  #syncToSelectedOptions() {
    const selectedOptions = this.#selectedOptions();
    const variant = this.variants.find((item) => this.#optionsMatch(item.options, selectedOptions));

    this.#updateVariant(variant || null);
  }

  /**
   * @returns {string[]}
   */
  #selectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('[data-wholesale-option-index]'));

    return fieldsets.map((fieldset) => {
      const checkedInput = fieldset.querySelector('input[data-wholesale-option]:checked');
      return checkedInput instanceof HTMLInputElement ? checkedInput.value : '';
    });
  }

  #optionsMatch(/** @type {string[]} */ variantOptions, /** @type {string[]} */ selectedOptions) {
    if (!Array.isArray(variantOptions) || variantOptions.length !== selectedOptions.length) return false;

    return variantOptions.every((option, index) => option === selectedOptions[index]);
  }

  #updateVariant(/** @type {WholesaleVariant | null} */ variant) {
    const isAvailable = Boolean(variant?.available);

    if (this.variantIdInput instanceof HTMLInputElement) {
      this.variantIdInput.value = variant?.id || '';
      this.variantIdInput.disabled = !isAvailable;
    }

    this.#updatePrice(variant);
    this.#updateProductLinks(variant);
    this.#updateQuantity(variant);
    this.#updateAddToCart(isAvailable);
  }

  #updatePrice(/** @type {WholesaleVariant | null} */ variant) {
    if (variant?.priceFormatted && this.priceElement) {
      this.priceElement.textContent = variant.priceFormatted;
    }

    const showComparePrice = Boolean(variant?.compareAtPriceFormatted);
    if (this.comparePriceElement) {
      this.comparePriceElement.textContent = variant?.compareAtPriceFormatted || '';
      this.comparePriceElement.toggleAttribute('hidden', !showComparePrice);
    }
  }

  #updateProductLinks(/** @type {WholesaleVariant | null} */ variant) {
    if (!variant?.url) return;

    this.productLinks.forEach((link) => {
      link.href = variant.url;
    });
  }

  #updateQuantity(/** @type {WholesaleVariant | null} */ variant) {
    const rule = variant?.quantityRule || {};
    const min = Number(rule.min || 1);
    const increment = Number(rule.increment || 1);
    const max = rule.max == null ? null : Number(rule.max);

    if (this.quantitySelector?.updateConstraints) {
      this.quantitySelector.updateConstraints(String(min), max == null ? null : String(max), String(increment));
    }

    if (this.quantityInput instanceof HTMLInputElement) {
      this.quantityInput.min = String(min);
      this.quantityInput.step = String(increment);

      if (max == null) {
        this.quantityInput.removeAttribute('max');
      } else {
        this.quantityInput.max = String(max);
      }

      const currentValue = Number(this.quantityInput.value || min);
      if (currentValue < min) this.quantityInput.value = String(min);
    }
  }

  #updateAddToCart(/** @type {boolean} */ isAvailable) {
    if (this.addToCartButton instanceof HTMLButtonElement) {
      this.addToCartButton.disabled = !isAvailable;
    }
  }
}

if (!customElements.get('wholesale-product-card')) {
  customElements.define('wholesale-product-card', WholesaleProductCard);
}
